import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  ApiError,
  type MatchEventWire,
  type MatchPublicView,
} from '../api-client.js';
import { useUserDisplayName } from '../users/use-user-profile.js';

/**
 * Match replay viewer.
 *
 * Walks through every engine event for a finished (or in-progress)
 * match — slider, prev/next buttons, keyboard arrows. Highlights
 * the current event with its full payload, and lists the
 * surrounding events as context.
 *
 * Real state-at-step reconstruction would need engine support
 * (snapshots are stored but the engine doesn't expose a "view at
 * sequence N" endpoint yet). This first cut is event-centric:
 * the type, payload, and a human-readable summary if one is in
 * the payload.
 *
 * Public route — same posture as MatchPage; spectators can replay
 * any match by URL.
 */
export default function ReplayPage() {
  const { matchId = '' } = useParams();
  const [match, setMatch] = useState<MatchPublicView | null>(null);
  const [events, setEvents] = useState<MatchEventWire[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);

  useEffect(() => {
    if (!matchId) return;
    let alive = true;
    Promise.all([api.getMatch(matchId), api.listEvents(matchId)])
      .then(([m, evs]) => {
        if (!alive) return;
        setMatch(m);
        setEvents(evs);
        // Land on the last event so the user sees the finish first.
        setCursor(Math.max(0, evs.length - 1));
      })
      .catch((e) => {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, [matchId]);

  const max = events ? Math.max(0, events.length - 1) : 0;
  const clampedCursor = Math.min(cursor, max);
  const currentEvent = events?.[clampedCursor] ?? null;

  const goPrev = useCallback(() => setCursor((c) => Math.max(0, c - 1)), []);
  const goNext = useCallback(() => setCursor((c) => Math.min(max, c + 1)), [max]);
  const goStart = useCallback(() => setCursor(0), []);
  const goEnd = useCallback(() => setCursor(max), [max]);

  // Keyboard navigation.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target && (e.target as HTMLElement).tagName === 'INPUT') return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Home') {
        e.preventDefault();
        goStart();
      } else if (e.key === 'End') {
        e.preventDefault();
        goEnd();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goPrev, goNext, goStart, goEnd]);

  if (!matchId) return <p className="text-ec-bone">Missing match id.</p>;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-ec-face">
          {match ? `${match.game.name} — Replay` : 'Replay'}
        </h2>
        {match && <span className="text-sm text-ec-bone-dim">Match {match.match.id}</span>}
        <Link
          to={`/matches/${matchId}`}
          className="ml-auto font-display text-sm uppercase tracking-wide text-ec-brass hover:underline"
        >
          ← back to match
        </Link>
      </header>

      {error && (
        <div className="ec-surface p-3 text-sm text-ec-face">
          {error}
        </div>
      )}

      {match && events && (
        <ParticipantsLine match={match} />
      )}

      {!events && !error && (
        <p className="text-sm text-ec-bone-dim">Loading events…</p>
      )}

      {events && events.length === 0 && (
        <p className="text-sm text-ec-bone-dim">No events recorded for this match yet.</p>
      )}

      {events && events.length > 0 && (
        <>
          <ScrubBar
            cursor={clampedCursor}
            max={max}
            onChange={setCursor}
            onPrev={goPrev}
            onNext={goNext}
            onStart={goStart}
            onEnd={goEnd}
          />
          {currentEvent && <CurrentEventCard event={currentEvent} total={events.length} />}
          <EventList events={events} cursor={clampedCursor} onJump={setCursor} />
        </>
      )}
    </div>
  );
}

function ParticipantsLine({ match }: { match: MatchPublicView }) {
  return (
    <p className="text-xs text-ec-bone-dim">
      Participants:{' '}
      {match.participants.map((p, i) => (
        <span key={p.id}>
          {i > 0 && ', '}
          <ParticipantName userId={p.userId} />
        </span>
      ))}
      {match.match.completedAt && (
        <>
          {' · '}
          <span title={match.match.completedAt}>completed</span>
        </>
      )}
    </p>
  );
}

function ParticipantName({ userId }: { userId: string }) {
  const name = useUserDisplayName(userId);
  return <span className="text-ec-brass">{name}</span>;
}

function ScrubBar({
  cursor,
  max,
  onChange,
  onPrev,
  onNext,
  onStart,
  onEnd,
}: {
  cursor: number;
  max: number;
  onChange: (n: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onStart: () => void;
  onEnd: () => void;
}) {
  return (
    <div className="ec-surface p-3">
      <div className="flex items-center gap-2 text-xs text-ec-bone">
        <NavButton onClick={onStart} disabled={cursor === 0} label="⏮" hint="Home" />
        <NavButton onClick={onPrev} disabled={cursor === 0} label="◀" hint="←" />
        <input
          type="range"
          min={0}
          max={max}
          value={cursor}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-ec-brass"
        />
        <NavButton onClick={onNext} disabled={cursor === max} label="▶" hint="→" />
        <NavButton onClick={onEnd} disabled={cursor === max} label="⏭" hint="End" />
        <span className="ml-2 font-mono text-ec-bone-dim">
          {cursor + 1} / {max + 1}
        </span>
      </div>
      <p className="mt-2 font-serif text-[10px] text-ec-bone-dim">
        Tip: arrow keys move one event; Home / End jump to ends.
      </p>
    </div>
  );
}

function NavButton({
  onClick,
  disabled,
  label,
  hint,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={hint}
      className="ec-btn !px-2 !py-1 text-xs"
    >
      {label}
    </button>
  );
}

function CurrentEventCard({ event, total }: { event: MatchEventWire; total: number }) {
  const summary =
    typeof (event.payload as { summary?: unknown })?.summary === 'string'
      ? ((event.payload as { summary: string }).summary)
      : null;
  return (
    <article className="rounded-lg border border-ec-brass bg-[rgba(245,168,28,0.12)] p-4">
      <header className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-xs text-ec-brass">
          #{event.sequence} of {total}
        </span>
        <h3 className="font-mono text-sm font-semibold text-ec-face">{event.type}</h3>
        <span
          className="ml-auto text-xs text-ec-bone-dim"
          title={event.createdAt}
        >
          {formatTime(event.createdAt)}
        </span>
      </header>
      {summary && <p className="mb-3 text-sm text-ec-face">{summary}</p>}
      <pre className="max-h-64 overflow-auto rounded-md bg-ec-ground p-3 text-xs text-ec-bone">
        {JSON.stringify(event.payload, null, 2)}
      </pre>
    </article>
  );
}

function EventList({
  events,
  cursor,
  onJump,
}: {
  events: MatchEventWire[];
  cursor: number;
  onJump: (n: number) => void;
}) {
  return (
    <section className="ec-surface overflow-hidden">
      <div className="ec-surface__head">
        <h3 className="ec-surface__title">All events</h3>
      </div>
      <ol className="max-h-96 divide-y divide-ec-line overflow-y-auto">
        {events.map((e, i) => {
          const active = i === cursor;
          return (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => onJump(i)}
                className={`flex w-full items-baseline gap-2 px-4 py-1.5 text-left text-[11px] ${
                  active
                    ? 'bg-[rgba(245,168,28,0.12)] text-ec-brass'
                    : 'text-ec-bone hover:bg-ec-ground/40'
                }`}
              >
                <span className="w-10 font-mono text-ec-bone-dim">#{e.sequence}</span>
                <span className="flex-1 truncate font-mono">{e.type}</span>
                <span className="font-mono text-ec-bone-dim">{formatTime(e.createdAt)}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso.slice(11, 19);
  }
}
